import { FC } from 'react';
import { cx } from '@emotion/css';
import { DSHeading, DSLinkButton, DSText } from '@stihl-design-system/components';
import { useTranslation } from 'next-i18next';

import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';
import { PhoenixImage } from '../../ui/assets/phoenix-image';
import { PromotionalBannerEntry } from '../../domain/static-content/static-content-api.model';
import { useImpressionEvent } from '../../util/hooks/use-impression-event';
import { usePromotionalBanner } from './use-promotional-banner';
import * as tracking from './promotional-banner.events';

interface PromotionalBannerProps {
  id: string;
  staticTags?: Array<string>;
  className?: string;
  baseEventName?: string;
}

export const PromotionalBanner: FC<PromotionalBannerProps> = ({ id, className, staticTags, baseEventName }) => {
  const { data: banner } = usePromotionalBanner(id);
  const { t } = useTranslation('shared');

  const {
    id: bannerId,
    type,
    headline,
    description,
    image,
    imagePosition,
    primaryButtonText,
    primaryButtonLinkout,
    secondaryButtonText,
    secondaryButtonLinkout,
  } = (banner || {}) as PromotionalBannerEntry;

  const imageFileName = image?.fileName;
  const isBgImage = imageFileName && imagePosition === 'background';
  const isAsideImage = imageFileName && (imagePosition === 'left' || imagePosition === 'right');

  const trackingData: tracking.EventData = { id: bannerId, type: type?.toLowerCase(), headline, description };
  const ref = useImpressionEvent(tracking.promotionalBannerImpression(baseEventName, trackingData));

  return bannerId ? (
    <section ref={ref} aria-label={headline} className={cx('relative bg-primary-orange py-11', className)}>
      {isBgImage && (
        <PhoenixImage src={imageFileName} layout="fill" objectFit="cover" objectPosition="center" staticTags={staticTags} sizes="100vw" />
      )}
      <div
        className={cx(
          'relative z-10 items-center overflow-hidden rounded-lg bg-primary-white stihl-page-container desktop:flex',
          imagePosition === 'right' && 'desktop:flex-row-reverse'
        )}
      >
        {isAsideImage && (
          <div className="relative aspect-[21/9] w-full flex-1 desktop:aspect-auto desktop:h-72 desktop:max-w-[22rem]">
            <PhoenixImage
              src={imageFileName}
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              staticTags={staticTags}
              sizes={`(max-width: ${containerQueries.tablet}) 100vw, 33vw`}
            />
          </div>
        )}
        <div className={cx('flex flex-1 flex-col gap-3 p-6', !isAsideImage && 'tablet:items-center tablet:text-center')}>
          <DSHeading>{headline}</DSHeading>
          <DSText>{description}</DSText>
          <DSLinkButton
            href={primaryButtonLinkout}
            target="_blank"
            onClick={tracking.promotionalBannerLinkClick(baseEventName, {
              ...trackingData,
              button_text: primaryButtonText,
              link_url: primaryButtonLinkout,
            })}
            iconName="arrow-out"
            iconPosition="right"
            data-testid={`primary-button-${bannerId}`}
          >
            {primaryButtonText} <span className="sr-only">{t('shared_a11y_hint_opensInNewTab')}</span>
          </DSLinkButton>
          {secondaryButtonText && secondaryButtonLinkout && (
            <DSLinkButton
              href={secondaryButtonLinkout}
              target="_blank"
              onClick={tracking.promotionalBannerSecondaryLinkClick(baseEventName, {
                ...trackingData,
                button_text: secondaryButtonText,
                link_url: secondaryButtonLinkout,
              })}
              variant="ghost"
              data-testid={`secondary-button-${bannerId}`}
              iconPosition="right"
              iconName="chevron-right"
            >
              {secondaryButtonText} <span className="sr-only">{t('shared_a11y_hint_opensInNewTab')}</span>
            </DSLinkButton>
          )}
        </div>
      </div>
    </section>
  ) : null;
};
